import React, { useRef, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { getProduct } from "../mock";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Upload, Wand2, ArrowRight, ChevronLeft, FileCheck2, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const DESIGN_FEE = 20;
const SIZES = ["S", "M", "L", "XL", "2XL", "3XL", "4XL"];

export default function DesignStudio() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const product = getProduct(slug);
  const { addItem } = useCart();
  const { toast } = useToast();
  const [color, setColor] = useState(() => product?.swatches?.find((s) => s.hex === params.get("color")) || product?.swatches?.[0]);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);
  const [option, setOption] = useState(null);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Product not found</h2>
          <Link to="/" className="link-peach mt-4 inline-flex">Back to home</Link>
        </div>
      </div>
    );
  }

  const base = parseFloat(String(product.price).replace(/[^0-9.]/g, ""));
  const unit = option === "create" ? base + DESIGN_FEE : base;
  const ready = (option === "upload" && file) || (option === "create" && notes.trim().length > 0);

  const addToCart = () => {
    if (!ready) return;
    const isCreate = option === "create";
    addItem({
      key: `${product.slug}-${size}-${color.hex}-${isCreate ? "create-" + Date.now() : file.id}`,
      slug: product.slug,
      title: `${product.title} — ${isCreate ? "Design by us" : "Your design"}`,
      price: unit.toFixed(2),
      img: product.img,
      color: color.hex,
      colorName: color.name,
      size,
      qty,
      customization: isCreate ? `We create the design (+$${DESIGN_FEE})` : `Uploaded design: ${file.filename}`,
      designOption: option,
      designFileId: isCreate ? null : file.id,
      designFileName: isCreate ? null : file.filename,
      designNotes: isCreate ? notes.trim() : null,
    });
    toast({ title: "Added to cart", description: "Review it before you check out." });
  };

  return (
    <div className="min-h-screen bg-[color:var(--dms-cream)]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-6">
        <Link to={`/shop/${slug}`} className="text-sm text-[color:var(--dms-navy)]/60 hover:text-[color:var(--dms-navy)] inline-flex items-center gap-1" data-testid="ds-back-link"><ChevronLeft className="w-4 h-4" /> Back to product</Link>
      </div>
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/10 aspect-square" style={{ background: color.hex }}>
          <img src={product.img} alt={product.title} className="w-full h-full object-cover mix-blend-multiply" data-testid="ds-preview" />
        </div>
        <div>
          <p className="kicker">Your design</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-2" data-testid="ds-title">{product.title}</h1>
          <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">Pick your color and size, then upload your design or let us make one for you.</p>

          <Label>Color · {color.name}</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {product.swatches.map((s) => (
              <button key={s.hex} onClick={() => setColor(s)} title={s.name} data-testid={`ds-color-${s.hex.replace("#", "")}`}
                className={`w-8 h-8 rounded-full border-2 transition-transform ${color.hex === s.hex ? "border-[color:var(--dms-peach)] scale-110" : "border-[color:var(--dms-navy)]/15"}`} style={{ background: s.hex }} />
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <Label>Size</Label>
              <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="ds-size-select" className="mt-2 w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-3 py-2.5 text-sm">
                {SIZES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Quantity</Label>
              <input type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} data-testid="ds-qty-input" className="mt-2 w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-3 py-2.5 text-sm" />
            </div>
          </div>

          <Label>How would you like your design?</Label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <OptionCard active={option === "upload"} onClick={() => setOption("upload")} icon={Upload} title="Upload my design" sub="PDF, PNG or JPG" testid="ds-option-upload" />
            <OptionCard active={option === "create"} onClick={() => setOption("create")} icon={Wand2} title="We will create design" sub={`+$${DESIGN_FEE} design fee`} testid="ds-option-create" />
          </div>

          {option === "upload" && <UploadBox file={file} setFile={setFile} />}
          {option === "create" && (
            <label className="block mt-4">
              <span className="text-xs text-[color:var(--dms-navy)]/65">Describe what you want: words, colors, logo ideas, style.</span>
              <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ds-notes-input" placeholder="e.g. Our church name in gold script with a small cross above it…"
                className="mt-2 w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--dms-peach)]" />
            </label>
          )}

          <div className="mt-7 flex items-center justify-between border-t border-[color:var(--dms-navy)]/10 pt-5">
            <div>
              <p className="text-xs text-[color:var(--dms-navy)]/55">Price each</p>
              <p className="font-serif text-2xl text-[color:var(--dms-navy)]" data-testid="ds-unit-price">${unit.toFixed(2)}</p>
            </div>
            <button onClick={addToCart} disabled={!ready} data-testid="ds-add-to-cart" className="btn-peach disabled:opacity-40 disabled:cursor-not-allowed">Add to cart <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Label({ children }) {
  return <p className="mt-6 text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{children}</p>;
}

function OptionCard({ active, onClick, icon: Icon, title, sub, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left rounded-xl border-2 p-4 transition-colors ${active ? "border-[color:var(--dms-peach)] bg-[color:var(--dms-peach)]/10" : "border-[color:var(--dms-navy)]/10 bg-white hover:border-[color:var(--dms-navy)]/30"}`}>
      <Icon className="w-5 h-5 text-[color:var(--dms-navy)]" />
      <p className="font-serif text-[color:var(--dms-navy)] mt-2">{title}</p>
      <p className="text-[11px] text-[color:var(--dms-navy)]/60 mt-0.5">{sub}</p>
    </button>
  );
}

function UploadBox({ file, setFile }) {
  const ref = useRef(null);
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) return toast({ title: "File too large", description: "Max 15 MB.", variant: "destructive" });
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/design-uploads`, fd);
      setFile(data);
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4">
      <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" className="hidden" onChange={onFile} data-testid="ds-file-input" />
      {file ? (
        <div className="flex items-center gap-3 rounded-xl border border-[color:var(--dms-peach)] bg-white p-3" data-testid="ds-file-uploaded">
          <FileCheck2 className="w-5 h-5 text-[color:var(--dms-peach)]" />
          <span className="flex-1 text-sm text-[color:var(--dms-navy)] truncate">{file.filename}</span>
          <button onClick={() => setFile(null)} data-testid="ds-file-remove" className="text-[color:var(--dms-navy)]/50 hover:text-[color:var(--dms-navy)]"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button onClick={() => ref.current?.click()} disabled={busy} data-testid="ds-upload-btn"
          className="w-full border-2 border-dashed border-[color:var(--dms-navy)]/20 rounded-xl p-6 text-center bg-white hover:border-[color:var(--dms-peach)] transition-colors">
          {busy ? <Loader2 className="w-6 h-6 mx-auto animate-spin text-[color:var(--dms-peach)]" /> : <Upload className="w-6 h-6 mx-auto text-[color:var(--dms-peach)]" />}
          <p className="mt-2 text-sm font-serif text-[color:var(--dms-navy)]">{busy ? "Uploading…" : "Choose your design file"}</p>
          <p className="text-[11px] text-[color:var(--dms-navy)]/55 mt-1">PDF, PNG or JPG · up to 15 MB</p>
        </button>
      )}
    </div>
  );
}
